import type { Notiz, NotizZiel } from "@gui-creator/gemeinsam";
import { useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { schluessel, useNotizen } from "../api/abfragen.ts";
import { api } from "../api/klient.ts";
import { Abschnitt } from "../eigenschaften/gemeinsam.tsx";
import { useDarf, useKonto } from "../huelle/konto.ts";
import { personFarbe } from "../live/Anwesenheit.tsx";
import { useHinweis } from "../ui/Hinweise.tsx";
import "./planung.css";

const zeit = new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" });

type Ziel = { art: NotizZiel["art"]; id: string };

function NotizEintrag({ n, ziel, onSlot }: { n: Notiz; ziel: Ziel; onSlot?: ((i: number) => void) | undefined }) {
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const konto = useKonto();
  const admin = useDarf("freigeben");
  const darfNotiz = useDarf("notiz");
  const neuLaden = () =>
    Promise.all([qc.invalidateQueries({ queryKey: schluessel.notizen(ziel.art, ziel.id) }), qc.invalidateQueries({ queryKey: ["todos"] }), qc.invalidateQueries({ queryKey: schluessel.struktur })]);
  const ausfuehren = async (f: () => Promise<unknown>) => {
    try {
      await f();
      await neuLaden();
    } catch (e) {
      hinweis({ text: (e as Error).message, art: "fehler" });
    }
  };
  return (
    <li className={`notiz${n.erledigt ? " notiz-erledigt" : ""}`}>
      {n.todo ? (
        <input
          type="checkbox"
          className="notiz-haken"
          checked={n.erledigt}
          disabled={!darfNotiz}
          aria-label={`Erledigt: ${n.text}`}
          onChange={(e) => void ausfuehren(() => api(`/api/notizen/${n.id}`, { methode: "PATCH", body: { erledigt: e.target.checked } }))}
        />
      ) : (
        <span className="notiz-person" style={{ background: personFarbe(n.autorId) }} aria-hidden="true">
          {n.autorName.slice(0, 1).toUpperCase()}
        </span>
      )}
      <div className="notiz-rumpf">
        <p className="notiz-text">{n.text}</p>
        <p className="notiz-kopf">
          {n.autorName} · <time dateTime={n.erstelltAm}>{zeit.format(new Date(n.erstelltAm))}</time>
          {n.ziel.slot !== undefined && onSlot && (
            <>
              {" · "}
              <button type="button" className="notiz-slot" onClick={() => onSlot(n.ziel.slot!)}>
                Slot {n.ziel.slot}
              </button>
            </>
          )}
          {n.erledigt && n.erledigtVon && <> · erledigt von {n.erledigtVon}</>}
        </p>
      </div>
      {(n.autorId === konto.kontoId || admin) && (
        <button type="button" className="knopf knopf-still knopf-symbol notiz-loeschen" aria-label="Notiz löschen" title="Notiz löschen" onClick={() => void ausfuehren(() => api(`/api/notizen/${n.id}`, { methode: "DELETE" }))}>
          <Trash2 size={13} aria-hidden="true" />
        </button>
      )}
    </li>
  );
}

/** Notizen und To-dos eines Ziels; mit `slot` nur die Notizen dieses Slots. */
export function Notizen({ ziel, slot = null, onSlot, offen = true }: { ziel: Ziel; slot?: number | null; onSlot?: (i: number) => void; offen?: boolean }) {
  const id = useId();
  const qc = useQueryClient();
  const hinweis = useHinweis();
  const darf = useDarf("notiz");
  const liste = useNotizen(ziel.art, ziel.id);
  const [text, setText] = useState("");
  const [todo, setTodo] = useState(false);
  const [sendet, setSendet] = useState(false);

  const sichtbar = (liste.data ?? []).filter((n) => slot === null || n.ziel.slot === slot);
  const offenZahl = sichtbar.filter((n) => n.todo && !n.erledigt).length;

  const absenden = async (e?: FormEvent) => {
    e?.preventDefault();
    const t = text.trim();
    if (!t || sendet) return;
    setSendet(true);
    try {
      await api("/api/notizen", { methode: "POST", body: { ziel: { ...ziel, ...(slot !== null ? { slot } : {}) }, text: t, todo } });
      setText("");
      setTodo(false);
      await Promise.all([qc.invalidateQueries({ queryKey: schluessel.notizen(ziel.art, ziel.id) }), qc.invalidateQueries({ queryKey: ["todos"] }), qc.invalidateQueries({ queryKey: schluessel.struktur })]);
    } catch (f) {
      hinweis({ text: (f as Error).message, art: "fehler" });
    } finally {
      setSendet(false);
    }
  };

  return (
    <Abschnitt
      region
      offen={offen}
      titel={
        <>
          {slot === null ? "Notizen" : `Notizen zu Slot ${slot}`}
          {offenZahl > 0 && <span className="pruef-zahl">{offenZahl} offen</span>}
        </>
      }
    >
      {sichtbar.length > 0 && (
        <ul className="notizen">
          {sichtbar.map((n) => (
            <NotizEintrag key={n.id} n={n} ziel={ziel} onSlot={slot === null ? onSlot : undefined} />
          ))}
        </ul>
      )}
      {liste.isSuccess && sichtbar.length === 0 && !darf && <p className="leise">Noch keine Notizen.</p>}
      {darf && (
        <form className="notiz-form" onSubmit={(e) => void absenden(e)}>
          <label htmlFor={`${id}-t`} className="unsichtbar">
            Neue Notiz
          </label>
          <textarea
            id={`${id}-t`}
            className="eingabe"
            rows={2}
            value={text}
            placeholder={slot === null ? "Notiz oder To-do …" : `Notiz zu Slot ${slot} …`}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) void absenden();
            }}
          />
          <div className="notiz-form-fuss">
            <label className="schalter">
              <input type="checkbox" checked={todo} onChange={(e) => setTodo(e.target.checked)} />
              <span>Als To-do</span>
            </label>
            <button type="submit" className="knopf" disabled={!text.trim() || sendet}>
              Hinzufügen
            </button>
          </div>
        </form>
      )}
    </Abschnitt>
  );
}
